import { makeAutoObservable, runInAction } from 'mobx'
import { AxiosError } from 'axios'
import { showToast } from 'shared/ui'
import { logger } from 'shared/lib'
import { SingletonRequest } from 'shared/api/lib'
import { BillingApi } from 'entities/Billing/api/billing'
import { Source } from 'entities/Billing/model/Source'
import { CreditPrice } from 'entities/Billing/model/CreditPrice'
import { AutorechargeStore } from 'entities/Billing/store/autorechargeStore'
import {
  type IParamsCreateSource,
  type IParamsUpdateBillingCredits,
  type IResponseBillingAddress,
  type IResponseBillingAreaCode,
  type IResponseBillingComplianceInfo,
  type IResponseBillingSource,
  type IResponseBillingState,
} from 'entities/Billing/api/types'
import { BillingPlansStore } from './billingPlansStore'

export class BillingStore {
  private _creditsPriceMap: Map<number, CreditPrice> = new Map()
  private _creditsPriceLoading = false
  private _statesMap: Map<string, IResponseBillingState> = new Map()
  private _areaCodesMap: Map<number, IResponseBillingAreaCode> = new Map()
  private _sourcesMap: Map<string, Source> = new Map()
  private _sourcesLoading = false
  private _autorechargeStore: AutorechargeStore | null = null
  private _autorechargeLoading = false
  private _buyCreditsLoading = false
  private _areaCodesLoading = false
  private _statesLoading = false
  private _billingComplianceInfo: IResponseBillingComplianceInfo | null = null
  private _billingPlansStore = new BillingPlansStore()
  private _billingAddress: IResponseBillingAddress = null

  constructor() {
    makeAutoObservable(this)
  }

  get billingComplianceInfo() {
    return this._billingComplianceInfo
  }

  get billingAddress() {
    return this._billingAddress
  }

  get isBillingComplianceMonthly() {
    return this._billingComplianceInfo?.interval === 'monthly'
  }

  get billingPlansStore() {
    return this._billingPlansStore
  }

  get creditsPriceLoading() {
    return this._creditsPriceLoading
  }

  get sourcesLoading() {
    return this._sourcesLoading
  }

  get autorecharge() {
    return this._autorechargeStore
  }

  get buyCreditsLoading() {
    return this._buyCreditsLoading
  }

  fetchPlans = async () => {
    await this._billingPlansStore.fetchPlans()
  }

  fetchBillingAdditionalCreditPrice = async () => {
    try {
      runInAction(() => {
        this._creditsPriceLoading = true
      })

      const { data } = await BillingApi.getBillingAdditionalCreditPrice()

      runInAction(() => {
        data.forEach((item) => {
          this._creditsPriceMap.set(item.credits, new CreditPrice(item))
        })
      })
    } catch (e) {
      console.log(e)
    } finally {
      runInAction(() => {
        this._creditsPriceLoading = false
      })
    }
  }

  applyCoupon = async () => {
    try {
      const currentUrl = window.location.href
      const queryString = currentUrl.split('?')[1]
      const searchParams = new URLSearchParams(queryString)

      if (searchParams.has('c')) {
        const { data } = await BillingApi.applyCoupon(searchParams.get('c') ?? '')
        if (data.error) {
          showToast({
            type: 'error',
            title: data.error,
          })
        } else {
          showToast({
            type: 'success',
            title: 'Coupon successfully applied!',
          })
        }
      }
    } catch (e) {
      const title =
        (e instanceof AxiosError && e.errors.getError('error')) || 'Error during coupon processing'

      showToast({
        type: 'error',
        title,
      })
    }
  }

  fetchBillingSources = async () => {
    try {
      runInAction(() => {
        this._sourcesLoading = true
      })

      const { data } = await BillingApi.getBillingSources()

      runInAction(() => {
        this._sourcesMap.clear()
        data.forEach((item) => {
          this._sourcesMap.set(item.id, new Source(item))
        })
      })
    } catch (e) {
      console.log(e)
    } finally {
      runInAction(() => {
        this._sourcesLoading = false
      })
    }
  }

  createSource = async (params: IParamsCreateSource) => {
    try {
      const { data } = await BillingApi.createSource(params)
      if (params.backup) {
        this.setBackupSource(data.id)
      }
      return this.addSource(data)
    } catch (e) {
      logger.error(e)
      throw e
    }
  }

  addSource = (sourceDate: IResponseBillingSource) => {
    const source = new Source(sourceDate)
    this._sourcesMap.set(source.id, source)
    if (source.primary) {
      this.setPrimarySource(source.id)
    }
    return source
  }

  fetchBillingAreaCodes = async () => {
    try {
      runInAction(() => {
        this._areaCodesLoading = true
      })

      const { data } = await BillingApi.getBillingAreaCodes()

      runInAction(() => {
        data.data.forEach((item) => {
          this._areaCodesMap.set(item.id, item)
        })
      })
    } catch (e) {
      console.log(e)
    } finally {
      runInAction(() => {
        this._areaCodesLoading = false
      })
    }
  }

  fetchBillingStates = async () => {
    try {
      runInAction(() => {
        this._statesLoading = true
      })

      const { data } = await BillingApi.getBillingStates()

      runInAction(() => {
        data.data.forEach((item) => {
          this._statesMap.set(item.state_code, item)
        })
      })
    } catch (e) {
      console.log(e)
    } finally {
      runInAction(() => {
        this._statesLoading = false
      })
    }
  }

  private _fetchAutorecharge = async () => {
    try {
      runInAction(() => {
        this._autorechargeLoading = true
      })

      const { data: autorecharge } = await BillingApi.getBillingAutoRecharge()

      runInAction(() => {
        this._autorechargeStore = new AutorechargeStore(autorecharge)
      })
    } catch (e) {
      console.log(e)
    } finally {
      runInAction(() => {
        this._autorechargeLoading = false
      })
    }
  }

  fetchAutorecharge = new SingletonRequest(this._fetchAutorecharge).request

  fetchBillingAddress = async () => {
    try {
      const { data } = await BillingApi.getBillingAddress()

      runInAction(() => {
        this._billingAddress = data
      })
    } catch (e) {
      console.log(e)
    }
  }

  private _fetchBillingComplianceInfo = async () => {
    try {
      const { data } = await BillingApi.getBillingComplianceInfo()

      this.setBillingComplianceInfo(data)
    } catch (e) {
      console.log(e)
    }
  }

  fetchBillingComplianceInfo = new SingletonRequest(this._fetchBillingComplianceInfo).request

  updateBillingComplianceInfo = async () => {
    try {
      const { data } = await BillingApi.updateBillingComplianceInfo()

      this.setBillingComplianceInfo(data)
    } catch (e) {
      logger.error(e)

      throw e
    }
  }

  setBillingComplianceInfo = (data: IResponseBillingComplianceInfo) => {
    this._billingComplianceInfo = data
  }

  setBillingAddress = (data: IResponseBillingAddress) => {
    this._billingAddress = data
  }

  handleBuyCredits = async (params: IParamsUpdateBillingCredits) => {
    try {
      runInAction(() => {
        this._buyCreditsLoading = true
      })

      return await BillingApi.updateBillingCredits(params)
    } catch (e) {
      logger.error(e)
      if (e instanceof AxiosError) {
        showToast({
          title: e.errors.getError('payment_method'),
          type: 'error',
        })

        return false
      }
    } finally {
      runInAction(() => {
        this._buyCreditsLoading = false
      })
    }
  }

  getCreditsPrice = (id: number) => {
    return this._creditsPriceMap.get(id) || null
  }

  getSources = (id: string) => {
    return this._sourcesMap.get(id) || null
  }

  setPrimarySource = (id: string) => {
    this._sourcesMap.forEach((source) => {
      source.setPrimary(id === source.id)
    })
  }

  setSourceAsPrimary = async (sourceId: string) => {
    try {
      await BillingApi.setSourcePrimary(sourceId)
      this.setPrimarySource(sourceId)
      showToast({
        type: 'success',
        title: 'Card set as primary payment method',
      })

      return true
    } catch (e) {
      logger.error(e)
      showToast({
        title: 'Failed to set card as primary',
        type: 'error',
      })

      return false
    }
  }

  deleteSource = async (sourceId: string) => {
    try {
      await BillingApi.deleteSource(sourceId)
      // https://salesmsg.atlassian.net/browse/SWC-15784 for synchronizing sources on the backend
      await this.fetchBillingSources()
      showToast({
        title: 'Card deleted',
        type: 'success',
      })
    } catch (e) {
      logger.error(e)
      showToast({
        title: 'Failed to delete card',
        type: 'error',
      })
    }
  }

  deleteAchSource = async (paymentMethodId: string) => {
    try {
      await BillingApi.deleteAchSource(paymentMethodId)
      await this.fetchBillingSources()
      showToast({ title: 'ACH deleted', type: 'success' })
    } catch (e) {
      logger.error(e)
      const errorMessage =
        e instanceof AxiosError && e.errors.getError('error')
          ? e.errors.getError('error')
          : 'Failed to delete ACH'

      showToast({ title: errorMessage, type: 'error' })
    }
  }

  setBackupSource = (id: string) => {
    this._autorechargeStore?.setBackup(id)
  }

  get creditsPrice() {
    return Array.from(this._creditsPriceMap.values())
  }

  get sources() {
    return Array.from(this._sourcesMap.values()).sort((source) => (source.isPrimary ? -1 : 1))
  }

  get sourcesSimple() {
    return Array.from(this._sourcesMap.values()).filter((item) => item.object === 'card')
  }

  get sourcesAch() {
    return Array.from(this._sourcesMap.values()).filter((item) => item.object === 'payment_method')
  }

  get states() {
    return Array.from(this._statesMap.values())
  }

  get areaCodes() {
    return Array.from(this._areaCodesMap.values())
  }
}
